import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Loader2, CheckCircle2, Download, AlertCircle } from 'lucide-react';
import { useTranslation } from '../context/TranslationContext';
import payText from '../data/payText';
import SourceChips from '../components/SourceChips';

var BACKEND = process.env.REACT_APP_BACKEND_URL;
var PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

function money(n) {
  return '$' + Number(n || 0).toFixed(2);
}

// Customer-facing page for an order Isaías took by phone or in person.
// The amount lives on the server — this page only approves it.
export default function PayOrderPage() {
  var params = useParams();
  var orderNumber = params.orderNumber;
  var [order, setOrder] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [paid, setPaid] = useState(false);
  var [downloads, setDownloads] = useState([]);
  var [busy, setBusy] = useState(false);
  var langRef = useTranslation();
  // The page speaks the language the order was taken in.
  var tx = payText[(order && order.language) || langRef.language] || payText.en;

  var load = useCallback(async function() {
    setLoading(true);
    try {
      var r = await fetch(BACKEND + '/api/manual-orders/' + encodeURIComponent(orderNumber) + '/public');
      var d = await r.json().catch(function() { return {}; });
      if (!r.ok) throw new Error(d.detail || 'Order not found');
      setOrder(d);
      setPaid(!!d.paid);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [orderNumber]);

  useEffect(function() { load(); }, [load]);

  async function createOrder() {
    setBusy(true); setError('');
    var r = await fetch(BACKEND + '/api/manual-orders/' + encodeURIComponent(orderNumber) + '/paypal/create', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
    });
    var d = await r.json().catch(function() { return {}; });
    if (!r.ok || !d.paypal_order_id) {
      setBusy(false);
      throw new Error(d.detail || 'PayPal could not start this payment');
    }
    return d.paypal_order_id;
  }

  async function onApprove(data) {
    try {
      var r = await fetch(BACKEND + '/api/manual-orders/' + encodeURIComponent(orderNumber) + '/paypal/capture', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paypal_order_id: data.orderID }),
      });
      var d = await r.json().catch(function() { return {}; });
      if (!r.ok) throw new Error(d.detail || 'Payment could not be completed');
      setPaid(true);
      setDownloads(d.download_links || []);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center text-neutral-400" data-testid="pay-page-loading">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> {tx.loading}
      </div>
    );
  }

  if (error && !order) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center px-6" data-testid="pay-page-error">
        <div className="max-w-md text-center">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
          <p className="text-white text-base mb-1">{tx.loadError}</p>
          <p className="text-neutral-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 px-5 py-14" data-testid="pay-order-page">
      <div className="max-w-xl mx-auto">
        <p className="text-[#d4af37] text-xs font-semibold tracking-[0.2em] uppercase mb-3">{tx.orderLabel} {order.order_number}</p>
        <h1 className="text-4xl sm:text-5xl font-serif text-white mb-8">
          {paid ? tx.thanks : tx.complete}
        </h1>

        <div className="bg-neutral-900/70 border border-white/10 rounded-2xl p-5 mb-6" data-testid="pay-order-summary">
          {(order.items || []).map(function(it, i) {
            return (
              <div key={it.slug + i} className="flex items-start justify-between gap-4 py-2.5 border-b border-white/5 last:border-0">
                <div>
                  <p className="text-neutral-100 text-sm">
                    {it.name}
                    {it.presale ? (
                      <span data-testid="pay-item-presale"
                            className="ml-2 align-middle inline-block text-[10px] tracking-wide text-[#d4af37] border border-[#d4af37]/60 rounded-full px-2 py-0.5">
                        {tx.preorder}
                      </span>
                    ) : null}
                  </p>
                  {it.variant ? <p className="text-neutral-500 text-xs mt-0.5">{it.variant}</p> : null}
                  <p className="text-neutral-500 text-xs mt-0.5">{tx.qty} {it.quantity}</p>
                </div>
                <p className="text-neutral-300 text-sm whitespace-nowrap">{money(it.unit_price * it.quantity)}</p>
              </div>
            );
          })}
          <div className="mt-4 space-y-1.5 text-sm">
            <Row label={tx.subtotal} value={money(order.subtotal)} />
            {order.shipping_cost ? <Row label={order.shipping_method_name || tx.shipping} value={money(order.shipping_cost)} /> : null}
            {order.tax_amount ? <Row label={tx.tax} value={money(order.tax_amount)} /> : null}
            {order.discount_amount ? <Row label={tx.discount} value={'− ' + money(order.discount_amount)} /> : null}
          </div>
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
            <span className="text-white font-semibold">{tx.total}</span>
            <span className="text-[#d4af37] text-2xl font-semibold" data-testid="pay-order-total">{money(order.total)}</span>
          </div>
        </div>

        {(order.items || []).some(function(i) { return i.presale; }) ? (
          <p className="text-neutral-400 text-sm bg-neutral-900/70 border-l-2 border-[#d4af37] rounded-r-xl px-4 py-3 mb-6"
             data-testid="pay-presale-note">
            {tx.presaleNote}
          </p>
        ) : null}

        {!paid && order.needs_address ? (
          <AddressForm orderNumber={order.order_number} onSaved={load} tx={tx} />
        ) : null}

        {paid ? (          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-5" data-testid="pay-order-paid">
            <p className="flex items-center gap-2 text-emerald-300 font-semibold mb-2">
              <CheckCircle2 className="w-5 h-5" /> {tx.received}
            </p>
            <p className="text-emerald-100/80 text-sm">
              {tx.receiptOnWay}{order.customer_email ? ' (' + order.customer_email + ')' : ''}.
            </p>
            <div className="mt-6 text-left">
              <SourceChips orderNumber={order.order_number} email={order.customer_email} />
            </div>
            {downloads.length ? (
              <div className="mt-4 space-y-2">
                {downloads.map(function(d) {
                  return (
                    <a key={d.url} href={d.url} data-testid="pay-order-download"
                       className="flex items-center gap-2 bg-black/40 hover:bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-sm text-neutral-100 transition-colors">
                      <Download className="w-4 h-4 text-[#d4af37]" /> {d.name}
                    </a>
                  );
                })}
              </div>
            ) : null}
          </div>
        ) : order.payment_method_type === 'paypal' ? (
          <div data-testid="pay-order-paypal" className={order.needs_address ? 'opacity-40 pointer-events-none' : ''}>
            {PAYPAL_CLIENT_ID ? (
              <PayPalScriptProvider options={{ 'client-id': PAYPAL_CLIENT_ID, currency: 'USD', intent: 'capture' }}>
                <PayPalButtons
                  style={{ layout: 'vertical', color: 'gold', shape: 'pill', label: 'paypal', height: 48 }}
                  disabled={busy}
                  forceReRender={[order.total]}
                  createOrder={createOrder}
                  onApprove={onApprove}
                  onError={function(e) { setBusy(false); setError(String(e && e.message ? e.message : e)); }}
                  onCancel={function() { setBusy(false); }}
                />
              </PayPalScriptProvider>
            ) : (
              <p className="text-red-400 text-sm">{tx.paypalMissing}</p>
            )}
            <p className="text-neutral-500 text-xs text-center mt-3">
              {tx.popupHint}
            </p>
          </div>
        ) : (
          <div className="bg-neutral-900/70 border border-white/10 rounded-2xl p-5" data-testid="pay-order-offline">
            <p className="text-neutral-100 text-sm mb-1">
              {order.payment_method_type === 'cash' ? tx.cash : tx.bank}
            </p>
            <p className="text-neutral-500 text-sm">
              {tx.offlineNote}
            </p>
          </div>
        )}

        {error ? <p className="mt-4 text-red-300 text-sm" data-testid="pay-order-error">{error}</p> : null}
      </div>
    </div>
  );
}

function Row(props) {
  return (
    <div className="flex items-center justify-between text-neutral-400">
      <span>{props.label}</span>
      <span>{props.value}</span>
    </div>
  );
}

var ADDR_FIELDS = ['first_name', 'last_name', 'street_address', 'extended_address',
  'locality', 'region', 'postal_code', 'phone'];

// Shown when the order has something to ship but no address yet: the customer
// fills it in and we price real shipping before they pay.
function AddressForm(props) {
  var tx = props.tx;
  var [form, setForm] = useState({ country_code_alpha2: 'US' });
  var [saving, setSaving] = useState(false);
  var [err, setErr] = useState('');

  function set(key, value) {
    setForm(function(prev) { return Object.assign({}, prev, { [key]: value }); });
  }

  async function submit() {
    if (!form.street_address || !form.locality || !form.postal_code) {
      setErr(tx.addrErr);
      return;
    }
    setSaving(true); setErr('');
    try {
      var r = await fetch(BACKEND + '/api/manual-orders/' + encodeURIComponent(props.orderNumber) + '/address', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shipping_address: form }),
      });
      var d = await r.json().catch(function() { return {}; });
      if (!r.ok) throw new Error(d.detail || 'We could not price shipping to that address');
      props.onSaved();
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-neutral-900/70 border border-[#d4af37]/30 rounded-2xl p-5 mb-6" data-testid="pay-order-address-form">
      <p className="text-white text-base mb-1">{tx.shipWhere}</p>
      <p className="text-neutral-500 text-sm mb-4">
        {tx.shipWhy}
      </p>
      <div className="grid sm:grid-cols-2 gap-2">
        {ADDR_FIELDS.map(function(f) {
          return (
            <input key={f} placeholder={tx['f_' + f]} value={form[f] || ''}
                   data-testid={'pay-addr-' + f.replace(/_/g, '-')}
                   onChange={function(e) { set(f, e.target.value); }}
                   className="w-full bg-black border border-white/15 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#d4af37]" />
          );
        })}
      </div>
      <button onClick={submit} disabled={saving} data-testid="pay-addr-submit"
              className="mt-4 w-full flex items-center justify-center gap-2 bg-[#d4af37] text-black font-semibold py-3 rounded-full hover:bg-white disabled:opacity-40 transition-colors">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {saving ? tx.savingAddr : tx.saveAddr}
      </button>
      {err ? <p className="mt-3 text-red-300 text-sm" data-testid="pay-addr-error">{err}</p> : null}
    </div>
  );
}
